import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown, FiSettings } from 'react-icons/fi';
import { cn } from '../../utils/tailwindMerge';

/**
 * Header 컴포넌트
 * 로고, 네비게이션 메뉴, 모바일 햄버거 메뉴를 포함하는 헤더
 */
const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownTimer, setDropdownTimer] = useState(null);

  // 네비게이션 메뉴 구조
  const navigationMenu = [
    { name: '홈', path: '/', hasDropdown: false },
    {
      name: '병원소개',
      path: '/about',
      hasDropdown: true,
      submenu: [
        { name: '인사말', path: '/about#greeting' },
        { name: '비전·연혁', path: '/about#history' }
      ]
    },
    {
      name: '의료진소개',
      path: '/doctors',
      hasDropdown: false
    },
    {
      name: '진료안내',
      path: '/treatment',
      hasDropdown: true,
      submenu: [
        { name: '시력교정클리닉', path: '/treatment/1' },
        { name: '백내장클리닉', path: '/treatment/2' },
        { name: '소아안과', path: '/treatment/3' },
        { name: '안질환센터', path: '/treatment/4' },
        { name: '검사·치료 안내', path: '/treatment/5' }
      ]
    },
    {
      name: '병원소식',
      path: '/notice',
      hasDropdown: true,
      submenu: [
        { name: '공지사항', path: '/notice?category=공지사항' },
        { name: '언론보도', path: '/notice?category=언론보도' }
      ]
    },
    {
      name: '병원안내',
      path: '/info',
      hasDropdown: true,
      submenu: [
        { name: '진료시간', path: '/info#hours' },
        { name: '오시는 길', path: '/info#location' }
      ]
    },
    {
      name: '상담문의',
      path: '/contact',
      hasDropdown: false
    }
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleMouseEnter = (index) => {
    // 타이머가 있다면 취소
    if (dropdownTimer) {
      clearTimeout(dropdownTimer);
      setDropdownTimer(null);
    }
    setActiveDropdown(index);
  };

  const handleMouseLeave = () => {
    // 즉시 닫지 않고 300ms 후에 닫음
    const timer = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
    setDropdownTimer(timer);
  };

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (dropdownTimer) {
        clearTimeout(dropdownTimer);
      }
    };
  }, [dropdownTimer]);

  return (
    <header className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* 로고 */}
          <Link
            to="/"
            className="flex items-center space-x-2 group"
            onClick={closeMobileMenu}
          >
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-primary group-hover:text-secondary transition-colors">
                Trenet Eye Clinic
              </span>
              <span className="text-sm text-gray-600">트레넷 안과의원</span>
            </div>
          </Link>

          {/* 데스크톱 네비게이션 */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navigationMenu.map((item, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => item.hasDropdown && handleMouseEnter(index)}
                onMouseLeave={() => item.hasDropdown && handleMouseLeave()}
              >
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center space-x-1 py-2 text-gray-700 hover:text-primary transition-colors font-medium',
                      isActive && 'text-primary'
                    )
                  }
                >
                  <span>{item.name}</span>
                  {item.hasDropdown && (
                    <FiChevronDown
                      className={cn(
                        'w-4 h-4 transition-transform',
                        activeDropdown === index && 'rotate-180'
                      )}
                    />
                  )}
                </NavLink>

                {/* 드롭다운 메뉴 */}
                {item.hasDropdown && activeDropdown === index && (
                  <div className="absolute top-full left-0 mt-0 pt-2 bg-white shadow-lg rounded-lg py-2 min-w-[200px] border border-gray-100">
                    {item.submenu.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Admin 버튼 */}
            <Link
              to="/admin/login"
              className="flex items-center space-x-1 px-4 py-2 bg-gray-100 hover:bg-primary hover:text-white text-gray-700 rounded-lg transition-colors font-medium"
            >
              <FiSettings className="w-4 h-4" />
              <span>Admin</span>
            </Link>
          </nav>

          {/* 모바일 햄버거 메뉴 버튼 */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-gray-700 hover:text-primary transition-colors"
            aria-label="메뉴 열기/닫기"
          >
            {isMobileMenuOpen ? (
              <FiX className="w-6 h-6" />
            ) : (
              <FiMenu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* 모바일 네비게이션 */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden border-t border-gray-200 py-4">
            {navigationMenu.map((item, index) => (
              <div key={index} className="mb-2">
                <div className="flex items-center justify-between">
                  <NavLink
                    to={item.path}
                    onClick={!item.hasDropdown ? closeMobileMenu : undefined}
                    className={({ isActive }) =>
                      cn(
                        'block py-2 text-gray-700 hover:text-primary transition-colors font-medium flex-1',
                        isActive && 'text-primary'
                      )
                    }
                  >
                    {item.name}
                  </NavLink>
                  {item.hasDropdown && (
                    <button
                      onClick={() => toggleDropdown(index)}
                      className="p-2 text-gray-700 hover:text-primary"
                      aria-label="서브메뉴 열기/닫기"
                    >
                      <FiChevronDown
                        className={cn(
                          'w-4 h-4 transition-transform',
                          activeDropdown === index && 'rotate-180'
                        )}
                      />
                    </button>
                  )}
                </div>

                {/* 모바일 서브메뉴 */}
                {item.hasDropdown && activeDropdown === index && (
                  <div className="pl-4 mt-2 space-y-2">
                    {item.submenu.map((subItem, subIndex) => (
                      <Link
                        key={subIndex}
                        to={subItem.path}
                        onClick={closeMobileMenu}
                        className="block py-2 text-sm text-gray-600 hover:text-primary transition-colors"
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Admin 버튼 (모바일) */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Link
                to="/admin/login"
                onClick={closeMobileMenu}
                className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-gray-100 hover:bg-primary hover:text-white text-gray-700 rounded-lg transition-colors font-medium"
              >
                <FiSettings className="w-4 h-4" />
                <span>Admin</span>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
